// TIP-611 — the order modal: a fixed header strip, four numbered sections in a
// scrolling body, and a fixed footer. On submit it switches IN PLACE to the
// confirmation and scrolls to the top; the form is not shown again unless the
// customer chooses `Order another brief`.
// THERE IS NO DRAFT AND NO SAVE. Cancel and the cross discard everything typed
// and deduct nothing. If a customer says they lost a half-finished order, that
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./counterpartyBriefs.css";
import CbButton from "./CbButton";
import CbCombobox from "./CbCombobox";
import CbDialPhone from "./CbDialPhone";
import CbField, { CbTextField } from "./CbField";
import OrderConfirmation from "./OrderConfirmation";
import ReferenceContactPanel from "./ReferenceContactPanel";
import {
  COUNTRY_LIST,
  EMIRATES,
  ORDER_COPY,
  PURPOSES,
  RELATIONSHIPS,
  ZONES,
  flagEmoji,
} from "./orderFormFixtures";
import { resolveSourceNote, resolveSpendNote } from "./orderFormState";
import { LABEL_BY_FIELD, ORDER_ERROR_RETRY } from "./orderPayload";
import useOrderForm from "./useOrderForm";

const plain = (list) => list.map((v) => ({ value: v, label: v }));

const Section = ({ n, title, children }) => (
  <section className={`cb-form-section${n === 1 ? " cb-form-section--first" : ""}`}>
    <div className="cb-form-section-head">
      <span className="cb-form-section-n" aria-hidden="true">
        {n}
      </span>
      <h3 className="cb-form-section-title">{title}</h3>
    </div>
    {children}
  </section>
);

const todayLabel = () =>
  new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

// Stands in for the backend's `TRX-CB-#####` on the fixture path, where there
// is no endpoint to answer. A live order always carries its own reference.
const stubReference = () => `TRX-CB-${Math.floor(10000 + Math.random() * 89999)}`;

/**
 * TIP-611 — why an order did not go through, and what to do about it.
 *
 * Every wording says whether anything was spent, because that is the customer's
 * first question. The retry is safe at any point: the idempotency key was
 * minted when this form opened, so pressing again is the same order.
 */
const SubmitError = ({ error, submitting, onRetry }) => {
  if (!error) return null;

  const rejected = Object.entries(error.fieldErrors || {});

  return (
    <div role="alert" data-testid="cb-order-error" className={`cb-order-error cb-order-error--${error.kind}`}>
      <p className="cb-order-error-heading">{error.heading}</p>
      <p className="cb-order-error-body">{error.body}</p>

      {/* Named, and in the customer's own words for each field, so a rejection
          can be found in a form of 24 fields without hunting. */}
      {rejected.length > 0 && (
        <ul className="cb-order-error-fields">
          {rejected.map(([field, message]) => (
            <li key={field}>
              <span className="cb-order-error-field">{LABEL_BY_FIELD[field] || field}</span>
              {`: ${message}`}
            </li>
          ))}
        </ul>
      )}

      {error.retryable && (
        <CbButton size="sm" disabled={submitting} onClick={onRetry}>
          {ORDER_ERROR_RETRY}
        </CbButton>
      )}
    </div>
  );
};

const OrderFormModal = ({
  allowance,
  onClose,
  onBuy,
  onSubmit,
  isSubmitting = false,
  // TIP-611 — section 1's five fields, from /api/user/me/ and the selected
  // company's details. Adopted as it arrives; the customer's own edits win.
  requesterProfile,
  // TIP-611 — the reference contact lookup, injected rather than reached for.
  // The store lives in the page; this modal takes props only, which is what
  // lets it be rendered in a test without a Provider. Omitted, the reference
  // panel behaves as it did before the endpoint existed.
  lookupReference,
  // The "you already have a brief for this business" check, injected the same
  // way as the reference lookup so this modal still takes props only. Omitted,
  // the subject-name box simply never warns.
  checkDuplicateSubject,
}) => {
  const form = useOrderForm(requesterProfile, { lookupReference, checkDuplicateSubject });
  const bodyRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // Guards the window between the press and the response, in which the button
  // would otherwise be pressable again. The idempotency key makes a second
  // order impossible, but a second in-flight request is still noise.
  const [sending, setSending] = useState(false);
  // Either this modal's own in-flight press, or the mutation reported busy by
  // the page. Both disable the footer.
  const busy = sending || isSubmitting;
  // A 409 means the bucket moved under the customer between opening this form
  // and pressing Send request — a colleague spent the last plan brief, so this
  // order would now come out of the bought bucket. The fresh allowance is
  // adopted and the customer confirms again. They are never charged money
  // having been told they were spending allowance.
  const [conflict, setConflict] = useState(null);
  const live = conflict || allowance;

  const countryOptions = useMemo(
    () => COUNTRY_LIST.map((c) => ({ value: c.name, label: c.name, leading: flagEmoji(c.code) })),
    []
  );
  const dialOptions = useMemo(
    () =>
      COUNTRY_LIST.map((c) => ({
        value: c.dial,
        label: c.name,
        sub: c.dial,
        leading: flagEmoji(c.code),
      })),
    []
  );

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toTop = () => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };

  const submit = async () => {
    if (!form.canSubmit) {
      form.setShowErrors(true);
      return;
    }
    if (sending) return;

    setSending(true);
    setSubmitError(null);
    form.setServerErrors({});

    let result = null;
    try {
      result = onSubmit
        ? await onSubmit({
            ...form.values,
            idempotencyKey: form.idempotencyKey,
            // The allowance as the FORM described it, so the backend can refuse
            // an order whose bucket changed while this was open.
            allowance: live,
          })
        : null;
    } finally {
      setSending(false);
    }

    if (result && result.error) {
      setSubmitError(result.error);
      form.setServerErrors(result.error.fieldErrors || {});
      // A conflict means the numbers moved. Adopting a fresh allowance here
      // keeps every wording in the form honest for the second press; where the
      // backend sends none, the card behind the modal is already refetching
      // and the `allowance` prop arrives on its own.
      if (result.error.kind === "conflict") setConflict(result.allowance || null);
      toTop();
      return;
    }

    setConflict(null);
    setSubmitError(null);
    setOrder({
      company:
        (result && result.order && result.order.company) || form.values.subjectName.trim(),
      reference: (result && result.order && result.order.reference) || stubReference(),
      // The backend's own created_at, formatted, so the confirmation and the
      // table row cannot disagree about when this was placed.
      placed: (result && result.order && result.order.ordered) || todayLabel(),
      // The allowance AS IT STOOD when the order was placed, which is what
      // deriveConfirmation() is documented to take: it subtracts the brief
      // just spent to work out what is left.
      // It has to be a snapshot. A placed order invalidates the allowance, so
      // the live `allowance` prop refetches to the already-decremented figure
      // moments later — and deriving from that subtracted the same brief
      // twice, turning "4 left this week" into "3 left this week".
      allowance: live,
    });
    toTop();
  };

  const orderAnother = () => {
    setOrder(null);
    form.reset();
    toTop();
  };

  const { requester, subject, request, sections, header, footer } = ORDER_COPY;

  return (
    <div className="cb-modal-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ORDER_COPY.title}
        data-testid="cb-order-modal"
        className="cb-modal cb-order-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {order ? (
          <OrderConfirmation
            order={order}
            allowance={order.allowance || live}
            onClose={onClose}
            onOrderAnother={orderAnother}
            onBuy={onBuy}
          />
        ) : (
          <>
            <div className="cb-order-head">
              <div>
                <span className="cb-modal-eyebrow">{ORDER_COPY.eyebrow}</span>
                <h2 className="cb-order-title">{ORDER_COPY.title}</h2>
                <p className="cb-order-standfirst" data-testid="cb-order-standfirst">
                  {ORDER_COPY.standfirst(resolveSourceNote(live))}
                </p>
              </div>
              <button
                type="button"
                className="cb-modal-close"
                onClick={onClose}
                aria-label={ORDER_COPY.close}
              >
                &times;
              </button>
            </div>

            <div className="cb-order-strip">
              <div className="cb-order-strip-item">
                <span className="cb-order-strip-label">{header.turnaroundLabel}</span>
                <span className="cb-order-strip-value">{header.turnaround}</span>
              </div>
              <div className="cb-order-strip-item">
                <span className="cb-order-strip-label">{header.dateLabel}</span>
                <span className="cb-order-strip-value">{todayLabel()}</span>
              </div>
              <div className="cb-order-strip-item cb-order-strip-item--wide">
                <span className="cb-order-strip-label">{header.referenceLabel}</span>
                <span className="cb-order-strip-value" data-testid="cb-order-reference">
                  {form.referenceSummary}
                </span>
              </div>
            </div>

            <div className="cb-order-body" ref={bodyRef}>
              {/* Above section 1, and scrolled to on failure, so a rejection
                  is never below the fold of a four-section form. */}
              <SubmitError error={submitError} submitting={busy} onRetry={submit} />

              {conflict && (
                <div className="cb-order-conflict" role="alert" data-testid="cb-order-conflict">
                  {resolveSourceNote(conflict)} Confirm to send this request.
                </div>
              )}

              <Section n={1} title={sections.one}>
                <div className="cb-grid">
                  <CbTextField
                    id="cb-full-name" error={form.errorFor("fullName")}
                    label={requester.fullName}
                    required
                    value={form.values.fullName}
                    onChange={form.onInput("fullName")}
                  />
                  <CbTextField
                    id="cb-job-title" error={form.errorFor("jobTitle")}
                    label={requester.jobTitle}
                    value={form.values.jobTitle}
                    placeholder={requester.jobTitlePlaceholder}
                    onChange={form.onInput("jobTitle")}
                  />
                  <CbTextField
                    id="cb-company" error={form.errorFor("company")}
                    label={requester.company}
                    required
                    value={form.values.company}
                    onChange={form.onInput("company")}
                  />
                  <CbField id="cb-email" error={form.errorFor("email")} label={requester.email} required>
                    <input
                      id="cb-email"
                      type="email"
                      className="cb-input"
                      value={form.values.email}
                      onChange={form.onInput("email")}
                    />
                  </CbField>
                  <CbDialPhone
                    id="cb-mobile" error={form.errorFor("mobile")}
                    testId="cb-mobile"
                    label={requester.mobile}
                    required
                    dial={form.values.dial}
                    phone={form.values.mobile}
                    options={dialOptions}
                    onDialChange={form.set("dial")}
                    onPhoneChange={form.set("mobile")}
                  />
                  <CbCombobox
                    id="cb-country" error={form.errorFor("country")}
                    testId="cb-country"
                    label={requester.country}
                    value={form.values.country}
                    options={countryOptions}
                    placeholder={requester.countryPlaceholder}
                    searchPlaceholder={requester.countrySearch}
                    onChange={form.set("country")}
                  />
                </div>
              </Section>

              <Section n={2} title={sections.two}>
                <div className="cb-grid">
                  <CbTextField
                    id="cb-subject-name" error={form.errorFor("subjectName")}
                    label={subject.legalName}
                    required
                    value={form.values.subjectName}
                    placeholder={subject.legalNamePlaceholder}
                    onChange={form.onInput("subjectName")}
                    onBlur={form.onSubjectNameBlur}
                    warning={form.duplicateWarning}
                  />
                  <CbTextField
                    id="cb-licence" error={form.errorFor("licence")}
                    label={subject.licence}
                    value={form.values.licence}
                    placeholder={subject.licencePlaceholder}
                    onChange={form.onInput("licence")}
                  />
                  <CbCombobox
                    id="cb-zone" error={form.errorFor("zone")}
                    testId="cb-zone"
                    label={subject.zone}
                    value={form.values.zone}
                    options={plain(ZONES)}
                    placeholder={subject.zonePlaceholder}
                    searchPlaceholder={subject.zoneSearch}
                    onChange={form.set("zone")}
                    disabled={form.subjectOutsideUAE}
                  />
                  <CbCombobox
                    id="cb-emirate" error={form.errorFor("emirate")}
                    testId="cb-emirate"
                    label={subject.emirate}
                    value={form.values.emirate}
                    options={plain(EMIRATES)}
                    placeholder={subject.emiratePlaceholder}
                    searchPlaceholder={subject.emirateSearch}
                    onChange={form.set("emirate")}
                    disabled={form.subjectOutsideUAE}
                  />
                  {/* Below the pair it explains, and only once one of them
                      has actually been answered on the customer's behalf. */}
                  {form.subjectOutsideUAE && (
                    <p className="cb-field cb-field--wide cb-subject-note" data-testid="cb-outside-uae-note">
                      {subject.outsideUaeNote}
                    </p>
                  )}
                  <CbCombobox
                    id="cb-subject-country" error={form.errorFor("subjectCountry")}
                    testId="cb-subject-country"
                    label={subject.country}
                    required
                    value={form.values.subjectCountry}
                    options={countryOptions}
                    placeholder={subject.countryPlaceholder}
                    searchPlaceholder={subject.countrySearch}
                    onChange={form.set("subjectCountry")}
                  />
                  <CbTextField
                    id="cb-website" error={form.errorFor("website")}
                    label={subject.website}
                    value={form.values.website}
                    placeholder={subject.websitePlaceholder}
                    onChange={form.onInput("website")}
                  />
                  {/* Only when the freezone answer is `Other freezone or authority`. */}
                  <CbTextField
                    id="cb-zone-other" error={form.errorFor("zoneOther")}
                    label={subject.zoneOther}
                    wide
                    hidden={!form.showZoneOther}
                    value={form.values.zoneOther}
                    placeholder={subject.zoneOtherPlaceholder}
                    onChange={form.onInput("zoneOther")}
                  />
                </div>

                <ReferenceContactPanel form={form} dialOptions={dialOptions} />
              </Section>

              <Section n={3} title={sections.three}>
                <div className="cb-grid">
                  <CbCombobox
                    id="cb-purpose" error={form.errorFor("purpose")}
                    testId="cb-purpose"
                    label={request.purpose}
                    value={form.values.purpose}
                    options={plain(PURPOSES)}
                    placeholder={request.purposePlaceholder}
                    searchPlaceholder={request.purposeSearch}
                    onChange={form.set("purpose")}
                  />
                  <CbCombobox
                    id="cb-relationship" error={form.errorFor("relationship")}
                    testId="cb-relationship"
                    label={request.relationship}
                    value={form.values.relationship}
                    options={plain(RELATIONSHIPS)}
                    placeholder={request.relationshipPlaceholder}
                    searchPlaceholder={request.relationshipSearch}
                    onChange={form.set("relationship")}
                  />
                  <CbTextField
                    id="cb-purpose-other" error={form.errorFor("purposeOther")}
                    label={request.purposeOther}
                    wide
                    hidden={!form.showPurposeOther}
                    value={form.values.purposeOther}
                    placeholder={request.purposePlaceholder}
                    onChange={form.onInput("purposeOther")}
                  />
                  <CbTextField
                    id="cb-relationship-other" error={form.errorFor("relationshipOther")}
                    label={request.relationshipOther}
                    wide
                    hidden={!form.showRelationshipOther}
                    value={form.values.relationshipOther}
                    placeholder={request.relationshipPlaceholder}
                    onChange={form.onInput("relationshipOther")}
                  />
                  {/* Non-digits are stripped and separators re-applied on every
                      keystroke, so `AED 250k` cannot reach the backend. */}
                  <CbField id="cb-exposure" error={form.errorFor("exposure")} label={request.exposure}>
                    <input
                      id="cb-exposure"
                      type="text"
                      inputMode="numeric"
                      className="cb-input"
                      value={form.values.exposure}
                      placeholder={request.exposurePlaceholder}
                      onChange={form.onExposure}
                    />
                  </CbField>
                  <CbTextField
                    id="cb-terms" error={form.errorFor("terms")}
                    label={request.terms}
                    value={form.values.terms}
                    placeholder={request.termsPlaceholder}
                    onChange={form.onInput("terms")}
                  />
                </div>
                <CbField id="cb-notes" error={form.errorFor("notes")} label={request.notes}>
                  <textarea
                    id="cb-notes"
                    rows="3"
                    className="cb-input cb-textarea"
                    value={form.values.notes}
                    placeholder={request.notesPlaceholder}
                    onChange={form.onInput("notes")}
                  />
                </CbField>
              </Section>

              <Section n={4} title={sections.four}>
                <div className="cb-declaration">
                  <p className="cb-declaration-text">{ORDER_COPY.declaration}</p>
                  <label className="cb-declaration-tick">
                    <input
                      type="checkbox"
                      checked={form.values.declaration}
                      onChange={(e) => form.set("declaration")(e.target.checked)}
                    />
                    <span>{ORDER_COPY.declarationTick}</span>
                  </label>
                </div>
              </Section>
            </div>

            <div className="cb-order-foot">
              <p className="cb-order-foot-note">
                {resolveSpendNote(live)} {footer.tail}
              </p>
              <div className="cb-order-foot-actions">
                {/* TIP-611 fix: the design never disables Send request, on any
                    condition. The tick box is a legal attestation, so the
                    button is disabled until it and the six required fields are
                    satisfied. */}
                {form.showErrors && !form.canSubmit && (
                  <p className="cb-order-foot-hint" role="alert">
                    {ORDER_COPY.blockedHint}
                  </p>
                )}
                <CbButton variant="secondary" disabled={busy} onClick={onClose}>
                  {footer.cancel}
                </CbButton>
                <CbButton
                  data-testid="cb-order-submit"
                  disabled={!form.canSubmit || busy}
                  onClick={submit}
                >
                  {busy ? footer.sending : footer.submit}
                </CbButton>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderFormModal;
